import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, ArrowRight, Mail } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { t } from '../translations';

// Nouveau logo Exevori
const LOGO = "https://customer-assets.emergentagent.com/job_connected-work/artifacts/kalrhzgp_remove.photos-removed-background%20Rxevori.png";

export default function Footer() {
  const { lang } = useLang();
  const tr = t[lang];

  const navLinks = [
    { label: tr.nav.home, to: '/' },
    { label: tr.nav.services, to: '/services' },
    { label: tr.nav.portfolio, to: '/realisations' },
    { label: tr.nav.process, to: '/processus' },
    { label: tr.nav.about, to: '/a-propos' },
    { label: tr.nav.faq, to: '/faq' },
    { label: tr.nav.contact, to: '/contact' },
  ];

  const services = lang === 'fr'
    ? ['Développement web', 'Applications sur mesure', 'Solutions SaaS', 'Automatisation', 'IA pour PME', 'Refonte digitale']
    : ['Web Development', 'Custom Applications', 'SaaS Solutions', 'Automation', 'AI for SMBs', 'Digital Redesign'];

  return (
    <footer data-testid="footer" style={{ backgroundColor: '#070F0F' }} className="border-t border-white/5">
      {/* CTA Banner */}
      <div
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0D1F1F 0%, #152040 50%, #0D1F1F 100%)' }}
      >
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'radial-gradient(circle at 30% 50%, #4F8EF7 0%, transparent 50%), radial-gradient(circle at 70% 50%, #9B59F5 0%, transparent 50%)',
          }}
        />
        <div className="section-container py-16 relative">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="font-sora font-bold text-2xl md:text-3xl text-white mb-4">
              {lang === 'fr' ? 'Prêt à démarrer votre projet ?' : 'Ready to start your project?'}
            </h3>
            <p className="text-gray-300 mb-8">
              {lang === 'fr'
                ? 'Contactez-nous dès aujourd\'hui pour discuter de vos besoins et obtenir une estimation gratuite.'
                : 'Contact us today to discuss your needs and get a free estimate.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/devis" data-testid="footer-cta-quote" className="btn-primary">
                {tr.nav.quote} <ArrowRight size={16} />
              </Link>
              <Link to="/contact" data-testid="footer-cta-contact" className="btn-secondary">
                {tr.nav.contact}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="section-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-5">
              <img src={LOGO} alt="Exevori" className="h-10 w-auto object-contain" />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {tr.footer.tagline}
            </p>
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                style={{ background: 'linear-gradient(to right, #4F8EF7, #9B59F5)' }}
              >
                <span className="text-white text-xs font-bold">in</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-sora font-semibold text-white mb-5 text-sm uppercase tracking-wider">
              {tr.footer.quickLinks}
            </h4>
            <ul className="space-y-3">
              {navLinks.map(link => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-link-${link.to.replace('/', '') || 'home'}`}
                    className="text-gray-400 hover:text-exevori-primary text-sm transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-3 overflow-hidden transition-all duration-200">
                      <ArrowRight size={12} className="text-exevori-primary" />
                    </span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-sora font-semibold text-white mb-5 text-sm uppercase tracking-wider">
              {tr.footer.ourServices}
            </h4>
            <ul className="space-y-3">
              {services.map((s, i) => (
                <li key={i}>
                  <Link
                    to="/services"
                    className="text-gray-400 hover:text-exevori-primary text-sm transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-3 overflow-hidden transition-all duration-200">
                      <ArrowRight size={12} className="text-exevori-primary" />
                    </span>
                    {s}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-sora font-semibold text-white mb-5 text-sm uppercase tracking-wider">
              {tr.footer.contact}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-exevori-primary mt-1 flex-shrink-0" />
                <span className="text-gray-400 text-sm">{tr.footer.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-exevori-primary flex-shrink-0" />
                <a
                  href="tel:+15817004171"
                  data-testid="footer-phone"
                  className="text-gray-400 hover:text-exevori-primary text-sm transition-colors"
                >
                  {tr.footer.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-exevori-primary flex-shrink-0" />
                <a
                  href="mailto:contact@exevori.com"
                  data-testid="footer-email"
                  className="text-gray-400 hover:text-exevori-primary text-sm transition-colors"
                >
                  {tr.footer.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock size={16} className="text-exevori-primary flex-shrink-0" />
                <span className="text-gray-400 text-sm">
                  {lang === 'fr' ? 'Lun–Ven : 9h–17h' : 'Mon–Fri: 9am–5pm'}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">{tr.footer.copyright}</p>
          <div className="flex items-center gap-6">
            <Link
              to="/legal"
              data-testid="footer-legal"
              className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
            >
              {tr.footer.legal}
            </Link>
            <Link
              to="/legal"
              data-testid="footer-privacy"
              className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
            >
              {tr.footer.privacy}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
